import { useMutation } from '@apollo/client';
import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Radio,
  RadioGroup,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { FC, useEffect } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import Datepicker from '../../../components/Form/Datepicker';
import { UPDATE_RECOMMENDATION_SHOWCASE } from '../../../graphQL/mutations';
import { GET_RECOMMENDATION_SHOWCASE } from '../../../graphQL/queries';
import schemaVitrine, {
  LIMITES,
  TAMANHO_MAXIMO_SUBTITULO,
  TAMANHO_MAXIMO_TITULO,
} from '../schema';
import {
  FormularioVitrine,
  ParametrosPreview,
  VitrineRecomendacao,
} from '../types';
import {
  formatarDataHora,
  formularioParaInput,
  formularioParaParametros,
  situacaoDaVitrine,
  SituacaoVitrine,
  TITULO_PADRAO,
  vitrineParaFormulario,
} from '../utils';
import CampoNumero from './CampoNumero';
import PreviewPanel from './PreviewPanel';
import Secao from './Secao';

interface Props {
  vitrine: VitrineRecomendacao;
}

const BADGE_SITUACAO: Record<SituacaoVitrine, { texto: string; cor: string }> =
  {
    'no-ar': { texto: 'No ar', cor: 'green' },
    desligada: { texto: 'Desligada', cor: 'gray' },
    'fora-da-agenda': { texto: 'Fora da agenda', cor: 'orange' },
  };

const CAMPOS_DO_ALGORITMO: (keyof FormularioVitrine)[] = [
  'rank_by',
  'mode',
  'min_cooccurrence',
  'months',
  'limit',
];

const ShowcaseForm: FC<Props> = ({ vitrine }) => {
  const toast = useToast();
  const contexto = vitrine.context;
  const ehPdp = contexto === 'pdp';

  const {
    control,
    formState: { errors, isDirty, isValid },
    getValues,
    handleSubmit,
    register,
    reset,
    trigger,
    watch,
  } = useForm<FormularioVitrine>({
    mode: 'onChange',
    resolver: yupResolver(schemaVitrine),
    defaultValues: vitrineParaFormulario(vitrine),
  });

  // quando a query volta com dados novos (ex.: depois de salvar), o form passa a refletir o salvo
  useEffect(() => {
    reset(vitrineParaFormulario(vitrine));
  }, [vitrine, reset]);

  // a validação do fim depende do início: revalida quando o início muda
  const inicio = watch('schedule_start');
  useEffect(() => {
    if (getValues('schedule_end')) trigger('schedule_end');
  }, [inicio, getValues, trigger]);

  const [atualizar, { loading: salvando }] = useMutation<{
    updateRecommendationShowcase: VitrineRecomendacao;
  }>(UPDATE_RECOMMENDATION_SHOWCASE, {
    // RecommendationShowcase não tem id e o Apollo não atualiza a query sozinho:
    // grava o retorno na query só deste contexto (a outra aba pode ter edições
    // não salvas). Sem refetch, uma falha de rede não vira erro num save que deu certo.
    update: (cache, { data }) => {
      if (!data?.updateRecommendationShowcase) return;
      cache.writeQuery({
        query: GET_RECOMMENDATION_SHOWCASE,
        variables: { context: contexto },
        data: { recommendationShowcase: data.updateRecommendationShowcase },
      });
    },
  });

  const salvar: SubmitHandler<FormularioVitrine> = async valores => {
    try {
      const { data } = await atualizar({
        variables: {
          context: contexto,
          input: formularioParaInput(valores, contexto),
        },
      });
      if (data?.updateRecommendationShowcase) {
        reset(vitrineParaFormulario(data.updateRecommendationShowcase));
      }
      toast({
        title: 'Sucesso!',
        description: 'Vitrine atualizada',
        status: 'success',
        position: 'top-right',
      });
    } catch (erro: any) {
      toast({
        title: 'Opss!!',
        description:
          erro?.message || 'Tivemos um erro, tente novamente mais tarde',
        status: 'error',
        position: 'top-right',
      });
    }
  };

  const obterParametros = async (): Promise<ParametrosPreview | null> => {
    const valido = await trigger(CAMPOS_DO_ALGORITMO);
    return valido ? formularioParaParametros(getValues(), contexto) : null;
  };

  const situacao = BADGE_SITUACAO[situacaoDaVitrine(vitrine)];

  return (
    <VStack
      as="form"
      spacing={6}
      align="stretch"
      sx={{ '.chakra-radio': { position: 'relative' } }}
      onSubmit={handleSubmit(salvar)}
    >
      <Flex align="center" gap={3} flexWrap="wrap">
        <Badge colorScheme={situacao.cor} fontSize="md" px={3} py={1}>
          {situacao.texto}
        </Badge>
        <Text color="gray.600" fontSize="sm">
          Última alteração: {formatarDataHora(vitrine.updated_at)}
        </Text>
      </Flex>

      <Secao titulo="Exibição">
        <VStack spacing={6} align="stretch">
          <FormControl display="flex" alignItems="center">
            <FormLabel htmlFor={`active-${contexto}`} mb="0">
              Vitrine ligada
            </FormLabel>
            <Controller
              name="active"
              control={control}
              render={({ field: { value, onChange, ref } }) => (
                <Switch
                  id={`active-${contexto}`}
                  size="lg"
                  colorScheme="brand"
                  isChecked={value}
                  onChange={evento => onChange(evento.target.checked)}
                  ref={ref}
                />
              )}
            />
          </FormControl>

          <SimpleGrid minChildWidth="260px" spacing={6}>
            <FormControl isInvalid={!!errors.title}>
              <FormLabel htmlFor={`title-${contexto}`}>Título</FormLabel>
              <Input
                id={`title-${contexto}`}
                bg="white"
                focusBorderColor="brand.500"
                maxLength={TAMANHO_MAXIMO_TITULO}
                placeholder={TITULO_PADRAO[contexto]}
                {...register('title')}
              />
              {errors.title ? (
                <FormErrorMessage>{errors.title.message}</FormErrorMessage>
              ) : (
                <FormHelperText>
                  Vazio usa o padrão: &quot;{TITULO_PADRAO[contexto]}&quot;
                </FormHelperText>
              )}
            </FormControl>
            <FormControl isInvalid={!!errors.subtitle}>
              <FormLabel htmlFor={`subtitle-${contexto}`}>Subtítulo</FormLabel>
              <Input
                id={`subtitle-${contexto}`}
                bg="white"
                focusBorderColor="brand.500"
                maxLength={TAMANHO_MAXIMO_SUBTITULO}
                placeholder="Opcional"
                {...register('subtitle')}
              />
              {!!errors.subtitle && (
                <FormErrorMessage>{errors.subtitle.message}</FormErrorMessage>
              )}
            </FormControl>
          </SimpleGrid>

          <SimpleGrid minChildWidth="260px" spacing={6}>
            <CampoNumero
              control={control}
              name="size"
              id={`size-${contexto}`}
              label="Quantidade máxima de produtos"
              min={LIMITES.size.min}
              max={LIMITES.size.max}
              ajuda="Quantos cards a vitrine mostra no máximo."
              error={errors.size}
              dependentes={['limit', 'min_products']}
            />
            <CampoNumero
              control={control}
              name="min_products"
              id={`min-products-${contexto}`}
              label="Mínimo de produtos para exibir"
              min={LIMITES.min_products.min}
              max={LIMITES.min_products.max}
              ajuda="Abaixo disso a vitrine não aparece."
              error={errors.min_products}
            />
          </SimpleGrid>

          <Box>
            <Text fontWeight="medium" mb={1}>
              Agenda
            </Text>
            <Text fontSize="sm" color="gray.600" mb={3}>
              A vitrine só aparece entre o início e o fim. Deixe o fim vazio
              para ela ficar no ar sem data para acabar.
            </Text>
            <SimpleGrid minChildWidth="220px" spacing={6}>
              <Datepicker
                isRequired
                control={control}
                name="schedule_start"
                label="Início"
                showTimeSelect
                error={errors.schedule_start}
              />
              <Datepicker
                control={control}
                name="schedule_end"
                label="Fim (opcional)"
                showTimeSelect
                error={errors.schedule_end}
                datePickerProps={{
                  isClearable: true,
                  placeholderText: 'Sem data de fim',
                }}
              />
            </SimpleGrid>
          </Box>
        </VStack>
      </Secao>

      {ehPdp && (
        <Secao
          titulo="Posição"
          descricao="Onde a vitrine aparece na página do produto."
        >
          <Controller
            name="position"
            control={control}
            render={({ field: { value, onChange } }) => (
              <RadioGroup value={value} onChange={onChange}>
                <Stack spacing={3}>
                  <Radio value="apos_avaliacoes" colorScheme="brand">
                    Depois das avaliações
                  </Radio>
                  <Radio value="apos_compre_junto" colorScheme="brand">
                    Depois do Compre junto
                  </Radio>
                </Stack>
              </RadioGroup>
            )}
          />
        </Secao>
      )}

      <Secao
        titulo="Algoritmo"
        descricao="Como os produtos sugeridos são escolhidos a partir dos pedidos de outros clientes."
      >
        <VStack spacing={6} align="stretch">
          <FormControl>
            <FormLabel>Ranking</FormLabel>
            <Controller
              name="rank_by"
              control={control}
              render={({ field: { value, onChange } }) => (
                <RadioGroup value={value} onChange={onChange}>
                  <Stack spacing={3}>
                    <Radio value="confidence" colorScheme="brand">
                      Mais comprados juntos (confiança){' '}
                      <Badge colorScheme="green">Recomendado</Badge>
                    </Radio>
                    <Radio value="lift" colorScheme="brand">
                      Associação mais forte (lift) — favorece itens de nicho
                    </Radio>
                  </Stack>
                </RadioGroup>
              )}
            />
            <FormHelperText>
              &quot;Confiança&quot; prioriza o que mais aparece junto nos
              pedidos. &quot;Lift&quot; prioriza combinações fora do comum,
              mesmo de produtos que vendem pouco.
            </FormHelperText>
          </FormControl>

          <FormControl>
            <FormLabel>Modo</FormLabel>
            {ehPdp ? (
              <>
                <Text fontWeight="medium">Lista única</Text>
                <FormHelperText>
                  A página do produto tem um produto só, então as sugestões
                  formam uma lista única.
                </FormHelperText>
              </>
            ) : (
              <>
                <Controller
                  name="mode"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <RadioGroup value={value} onChange={onChange}>
                      <Stack spacing={3}>
                        <Radio value="interleave" colorScheme="brand">
                          Intercalar por item da sacola{' '}
                          <Badge colorScheme="green">Recomendado</Badge>
                        </Radio>
                        <Radio value="any" colorScheme="brand">
                          Lista única
                        </Radio>
                        <Radio value="all" colorScheme="brand">
                          Só pedidos com todos os itens
                        </Radio>
                      </Stack>
                    </RadioGroup>
                  )}
                />
                <FormHelperText>
                  Intercalar: alterna sugestões de cada produto da sacola. Lista
                  única: junta pedidos com qualquer um dos produtos. Só pedidos
                  com todos os itens: mais preciso, mas costuma trazer poucas
                  sugestões.
                </FormHelperText>
              </>
            )}
          </FormControl>

          <SimpleGrid minChildWidth="260px" spacing={6}>
            <CampoNumero
              control={control}
              name="min_cooccurrence"
              id={`min-cooccurrence-${contexto}`}
              label="Mínimo de pedidos juntos"
              min={LIMITES.min_cooccurrence.min}
              max={LIMITES.min_cooccurrence.max}
              ajuda="Valores maiores = sugestões mais confiáveis, porém menos itens."
              error={errors.min_cooccurrence}
            />
            <FormControl isInvalid={!!errors.months}>
              <FormLabel htmlFor={`months-${contexto}`}>
                Janela de pedidos
              </FormLabel>
              <Select
                id={`months-${contexto}`}
                bg="white"
                focusBorderColor="brand.500"
                {...register('months', { valueAsNumber: true })}
              >
                <option value={1}>Último mês</option>
                <option value={2}>Últimos 2 meses</option>
                <option value={3}>Últimos 3 meses</option>
              </Select>
              {errors.months ? (
                <FormErrorMessage>{errors.months.message}</FormErrorMessage>
              ) : (
                <FormHelperText>
                  Período de pedidos considerado nas sugestões.
                </FormHelperText>
              )}
            </FormControl>
            <CampoNumero
              control={control}
              name="limit"
              id={`limit-${contexto}`}
              label="Produtos pedidos à recomendação"
              min={LIMITES.limit.min}
              max={LIMITES.limit.max}
              ajuda="Margem para descartar indisponíveis; deve ser maior ou igual à quantidade máxima."
              error={errors.limit}
            />
          </SimpleGrid>
        </VStack>
      </Secao>

      <PreviewPanel contexto={contexto} obterParametros={obterParametros} />

      <Flex gap={4} justify="flex-end" flexWrap="wrap" pb={4}>
        <Button
          variant="outline"
          isDisabled={!isDirty || salvando}
          onClick={() => reset(vitrineParaFormulario(vitrine))}
        >
          Restaurar valores salvos
        </Button>
        <Button
          type="submit"
          colorScheme="brand"
          isLoading={salvando}
          isDisabled={!isDirty || !isValid}
        >
          Salvar
        </Button>
      </Flex>
    </VStack>
  );
};

export default ShowcaseForm;
